import { Chip, Stack, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import moment from 'moment';

/**
 * Column definitions for the SkillX XP rules grid (admin configuration).
 *
 * Fields map to a skillx_xp_rules row as returned by
 * GET /student-portal/skillx/xp/rules, plus the two usage figures the
 * controller joins on (`event_count`, `xp_awarded`).
 *
 * Those two are the important columns: they are how an admin tells a live rule
 * from a seeded-but-never-wired one before retuning it. A rule with events
 * behind it can only be retired, never deleted.
 */
export const SkillxXpRules_Table_Head = [
  {
    field: 'label',
    headerName: 'Activity',
    width: 230,
    renderCell: (params) => (
      <Stack direction="row" alignItems="center" spacing={1}>
        {params.row.icon ? (
          <Icon icon={params.row.icon} width={18} height={18} color="#2E75BC" />
        ) : (
          <Icon icon="solar:bolt-bold-duotone" width={18} height={18} color="#8E9AAF" />
        )}
        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{params.row.label}</Typography>
      </Stack>
    ),
  },
  {
    field: 'rule_key',
    headerName: 'Rule Key',
    width: 175,
    renderCell: (params) => (
      <Tooltip arrow title="The contract with the producers — cannot be changed once set">
        <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: '#66708A' }}>
          {params.row.rule_key}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: 'points',
    headerName: 'Points',
    width: 110,
    renderCell: (params) => (
      <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#2E75BC' }}>
        {params.row.is_bonus ? `+${params.row.points}` : params.row.points}
      </Typography>
    ),
  },
  {
    field: 'is_bonus',
    headerName: 'Type',
    width: 105,
    renderCell: (params) => (
      <Tooltip
        arrow
        title={
          params.row.is_bonus
            ? 'Stacks on top of a base award rather than standing alone'
            : 'Awarded on its own'
        }
      >
        <Chip
          size="small"
          label={params.row.is_bonus ? 'Bonus' : 'Base'}
          sx={{
            backgroundColor: params.row.is_bonus ? '#fff1e0' : '#eff4f9',
            color: params.row.is_bonus ? '#C25E00' : '#3173b4',
            fontWeight: 600,
          }}
        />
      </Tooltip>
    ),
  },
  {
    field: 'allow_manual_award',
    headerName: 'Mentor Award',
    width: 140,
    renderCell: (params) => (
      <Tooltip
        arrow
        title={
          params.row.allow_manual_award
            ? 'Mentors can give this by hand from Award XP'
            : 'Awarded automatically by the portal. Award XP still lists it, for the times the automatic award did not land'
        }
      >
        <Chip
          size="small"
          icon={
            <Icon
              icon={
                params.row.allow_manual_award
                  ? 'solar:hand-stars-bold-duotone'
                  : 'solar:cpu-bolt-bold-duotone'
              }
              width={14}
              height={14}
            />
          }
          label={params.row.allow_manual_award ? 'Manual' : 'Automatic'}
          sx={{
            backgroundColor: params.row.allow_manual_award ? '#e8f3ff' : '#f4f6f8',
            color: params.row.allow_manual_award ? '#2E75BC' : '#66708A',
            fontWeight: 600,
          }}
        />
      </Tooltip>
    ),
  },
  {
    field: 'event_count',
    headerName: 'Awarded',
    width: 155,
    renderCell: (params) => {
      const count = params.row.event_count || 0;
      if (!count) {
        return (
          <Tooltip arrow title="Nothing has ever been awarded under this rule">
            <Typography sx={{ fontSize: 12.5, color: '#8E9AAF' }}>Never used</Typography>
          </Tooltip>
        );
      }
      return (
        <Stack>
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
            {count.toLocaleString()} times
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: '#66708A' }}>
            {(params.row.xp_awarded || 0).toLocaleString()} XP total
          </Typography>
        </Stack>
      );
    },
  },
  {
    field: 'is_active',
    headerName: 'Status',
    width: 115,
    renderCell: (params) => (
      <Chip
        size="small"
        label={params.row.is_active ? 'Active' : 'Retired'}
        sx={{
          backgroundColor: params.row.is_active ? '#c8facd' : '#f4f6f8',
          color: params.row.is_active ? '#0db05d' : '#66708A',
          fontWeight: 600,
        }}
      />
    ),
  },
  {
    field: 'updated_at',
    headerName: 'Last Changed',
    width: 140,
    renderCell: (params) =>
      params.row.updated_at ? moment(params.row.updated_at).format('DD MMM YYYY') : '—',
  },
];
